import { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FiArrowLeft, FiUsers, FiClock, FiCalendar, FiCoffee, FiFileText, FiChevronDown, FiChevronUp, FiX, FiPrinter, FiEye, FiTrash2, FiInfo } from 'react-icons/fi';
import Calendar from '../components/Calendar';
import Dropdown from '../components/Dropdown';
import ConfirmModal from '../components/ConfirmModal';
import './Pages.css';
import './Logs.css';
import './Attachments.css';

const PERIODS = [
  { key: 'today', label: 'დღეს' },
  { key: 'week', label: 'კვირა' },
  { key: 'month', label: 'თვე' },
  { key: 'all', label: 'ყველა' },
];

const fmt = (ms) => {
  const sec = Math.floor(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h === 0) return `${m} წთ`;
  return `${h} სთ ${m} წთ`;
};

const fmtLive = (ms) => {
  const sec = Math.floor(ms / 1000);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const fmtTime = (d) => d.toLocaleTimeString('ka-GE', { hour: '2-digit', minute: '2-digit', hour12: false });
const fmtDate = (d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;

const Logs = () => {
  const { isManager } = useAuth();
  const [usersList, setUsersList] = useState([]);
  const [cities, setCities] = useState([]);
  const [managers, setManagers] = useState([]);
  const [filterCity, setFilterCity] = useState('');
  const [filterManager, setFilterManager] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [period, setPeriod] = useState('today');
  const [activeTab, setActiveTab] = useState('sessions');
  const [expandedSession, setExpandedSession] = useState(null);
  const [showView, setShowView] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [, setTick] = useState(0);
  const todayStr = new Date().toISOString().split('T')[0];
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState(todayStr);

  // Live timer for active sessions (list + detail view)
  useEffect(() => {
    const hasActiveList = usersList.some((u) => u.current_status === 'active' || u.current_status === 'break');
    const hasActiveDetail = userDetail?.sessions?.some((s) => s.status === 'active' || s.status === 'break');
    if (!hasActiveList && !hasActiveDetail) return;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [usersList, userDetail]);

  const getUserStatus = (u) => {
    if (u.current_status === 'active') return 'active';
    if (u.current_status === 'break') return 'break';
    return 'inactive';
  };

  const getLiveTime = (u) => {
    if (!u.current_session_started) return null;
    const now = Date.now();
    const sessionStart = new Date(u.current_session_started).getTime();
    const totalElapsed = now - sessionStart;
    const breakSec = parseFloat(u.current_session_break_seconds) || 0;

    if (u.current_status === 'break' && u.current_break_started) {
      const breakStart = new Date(u.current_break_started).getTime();
      const currentBreak = now - breakStart;
      return { workMs: totalElapsed - (breakSec * 1000), breakMs: currentBreak };
    }
    return { workMs: totalElapsed - (breakSec * 1000), breakMs: 0 };
  };

  const filteredUsers = usersList.filter((u) => {
    if (statusFilter !== 'all' && getUserStatus(u) !== statusFilter) return false;
    if (filterCity && String(u.city_id) !== String(filterCity)) return false;
    if (filterManager) {
      const mgr = managers.find(m => String(m.id) === String(filterManager));
      if (mgr && String(u.city_id) !== String(mgr.city_id)) return false;
    }
    if (searchQuery && !`${u.first_name} ${u.last_name} ${u.personal_number}`.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  const counts = { active: 0, break: 0, inactive: 0 };
  usersList.forEach((u) => counts[getUserStatus(u)]++);

  const fetchUsers = async () => {
    try {
      const [usersRes, citiesRes, managersRes] = await Promise.all([
        api.get('/logs/users-list'),
        api.get('/cities').catch(() => ({ data: [] })),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setUsersList(usersRes.data);
      setCities(citiesRes.data);
      setManagers(managersRes.data.filter(u => u.role === 'manager'));
    } catch { /* ignored */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchUsers();
    const iv = setInterval(async () => {
      try { const { data } = await api.get('/logs/users-list'); setUsersList(data); } catch {}
    }, 30000);
    return () => clearInterval(iv);
  }, []);

  const openUserDetail = async (user) => {
    setSelectedUser(user);
    setDetailLoading(true);
    setPeriod('today');
    setActiveTab('sessions');
    setExpandedSession(null);
    try {
      const { data } = await api.get(`/logs/user-summary/${user.id}`);
      setUserDetail(data);
    } catch { /* ignored */ }
    finally { setDetailLoading(false); }
  };

  const goBack = () => {
    setSelectedUser(null);
    setUserDetail(null);
    fetchUsers();
  };

  // Filter sessions by period
  const filterByPeriod = (items, dateField) => {
    if (!items || period === 'all') return items;
    if (period === 'custom') {
      return items.filter((i) => {
        const d = new Date(i[dateField]);
        if (dateFrom && d < new Date(dateFrom)) return false;
        if (dateTo) { const end = new Date(dateTo); end.setHours(23, 59, 59, 999); if (d > end) return false; }
        return true;
      });
    }
    const now = new Date();
    let start;
    if (period === 'today') start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    else if (period === 'week') { const d = now.getDay(); const diff = d === 0 ? 6 : d - 1; start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff); }
    else if (period === 'month') start = new Date(now.getFullYear(), now.getMonth(), 1);
    return items.filter((i) => new Date(i[dateField]) >= start);
  };

  // Compute stats from sessions
  const computeStats = (sessions) => {
    let totalWork = 0, totalBreak = 0, sessionCount = 0;
    (sessions || []).forEach((s) => {
      if (!s.ended_at) return;
      sessionCount++;
      const start = new Date(s.started_at).getTime();
      const end = new Date(s.ended_at).getTime();
      let brk = 0;
      (s.breaks || []).forEach((b) => { if (b.ended_at) brk += new Date(b.ended_at) - new Date(b.started_at); });
      totalWork += (end - start) - brk;
      totalBreak += brk;
    });
    return { totalWork, totalBreak, sessionCount };
  };

  const openView = async (fa) => {
    try {
      const { data } = await api.get(`/filled-attachments/${fa.id}`);
      setViewData(data);
      setShowView(true);
    } catch { /* ignored */ }
  };

  const deleteSession = (s) => {
    const attCount = getSessionAttachments(s.id).length;
    setConfirmAction({
      title: 'სესიის წაშლა',
      message: `ნამდვილად გსურთ სესიის წაშლა?${attCount > 0 ? ` ამ სესიაში ${attCount} შევსებული დანართია, ისინიც წაიშლება!` : ''} ეს მოქმედება შეუქცევადია!`,
      type: 'delete',
      confirmText: 'წაშლა',
      onConfirm: async () => {
        try {
          await api.delete(`/work/session/${s.id}`);
          const { data } = await api.get(`/logs/user-summary/${selectedUser.id}`);
          setUserDetail(data);
        } catch { /* ignored */ }
        setConfirmAction(null);
      },
    });
  };

  const deleteAttachment = (fa) => {
    setConfirmAction({
      title: 'დანართის სამუდამო წაშლა',
      message: `ნამდვილად გსურთ "${fa.template_title || 'დანართი'}" სამუდამოდ წაშლა? ეს მოქმედება შეუქცევადია!`,
      type: 'delete',
      confirmText: 'წაშლა',
      onConfirm: async () => {
        try {
          await api.delete(`/filled-attachments/${fa.id}`);
          // Refresh user detail
          const { data } = await api.get(`/logs/user-summary/${selectedUser.id}`);
          setUserDetail(data);
        } catch { /* ignored */ }
        setConfirmAction(null);
      },
    });
  };

  const formatTimeSec = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const printAttachment = () => window.print();

  // Get filled attachments for a session
  const getSessionAttachments = (sessionId) => {
    if (!userDetail?.filledAttachments) return [];
    return userDetail.filledAttachments.filter((fa) => fa.session_id === sessionId);
  };

  // User list view
  if (!selectedUser) {
    return (
      <div className="page">
        <div className="page-header">
          <div>
            <h1>ისტორია</h1>
            <p className="page-subtitle">აირჩიეთ თანამშრომელი</p>
          </div>
          <div className="header-actions">
            <input className="search-input" placeholder="ძიება..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
            {!isManager && cities.length > 0 && (
              <Dropdown
                value={filterCity}
                onChange={(val) => setFilterCity(val)}
                options={[{ value: '', label: 'ყველა ქალაქი' }, ...cities.map((c) => ({ value: c.id, label: c.name }))]}
                placeholder="ქალაქი"
              />
            )}
            {!isManager && managers.length > 0 && (
              <Dropdown
                value={filterManager}
                onChange={(val) => setFilterManager(val)}
                options={[{ value: '', label: 'ყველა მენეჯერი' }, ...managers.map((m) => ({ value: m.id, label: `${m.first_name} ${m.last_name}` }))]}
                placeholder="მენეჯერი"
              />
            )}
          </div>
        </div>
        {loading ? <div className="loading">იტვირთება...</div> : (
          <>
            {/* Status filter tabs */}
            <div className="status-filter-tabs">
              <button className={`status-tab ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')}>
                ყველა <span className="status-tab-count">{usersList.length}</span>
              </button>
              <button className={`status-tab status-tab-active ${statusFilter === 'active' ? 'active' : ''}`} onClick={() => setStatusFilter('active')}>
                აქტიური <span className="status-tab-count">{counts.active}</span>
              </button>
              <button className={`status-tab status-tab-break ${statusFilter === 'break' ? 'active' : ''}`} onClick={() => setStatusFilter('break')}>
                შესვენება <span className="status-tab-count">{counts.break}</span>
              </button>
              <button className={`status-tab status-tab-inactive ${statusFilter === 'inactive' ? 'active' : ''}`} onClick={() => setStatusFilter('inactive')}>
                არააქტიური <span className="status-tab-count">{counts.inactive}</span>
              </button>
            </div>

            <div className="users-log-grid">
              {filteredUsers.map((u) => {
                const status = getUserStatus(u);
                const live = getLiveTime(u);
                const sessionStart = u.current_session_started ? fmtTime(new Date(u.current_session_started)) : null;

                return (
                  <div key={u.id} className={`user-log-card ${status !== 'inactive' ? 'ulc-' + status : ''}`} onClick={() => openUserDetail(u)}>
                    <div className="ulc-top">
                      <div className="ulc-avatar">{u.first_name[0]}{u.last_name[0]}</div>
                      <div className="ulc-info">
                        <h3>{u.first_name} {u.last_name}</h3>
                        <span className="ulc-pn">{u.personal_number}</span>
                      </div>
                      <span className={`ulc-badge ulc-badge-${status}`}>
                        {status === 'active' ? 'აქტიური' : status === 'break' ? 'შესვენება' : 'არააქტიური'}
                      </span>
                    </div>

                    {/* Live session info for active/break */}
                    {live && (
                      <div className="ulc-live">
                        <div className="ulc-live-row">
                          <span className="ulc-live-label">დაწყება</span>
                          <span className="ulc-live-val">{sessionStart} - ...</span>
                        </div>
                        <div className="ulc-live-row">
                          <span className="ulc-live-label"><FiClock /> სამუშაო</span>
                          <span className="ulc-live-val ulc-live-work">{fmtLive(Math.max(0, live.workMs))}</span>
                        </div>
                        {status === 'break' && (
                          <div className="ulc-live-row">
                            <span className="ulc-live-label"><FiCoffee /> შესვენება</span>
                            <span className="ulc-live-val ulc-live-break">{fmtLive(Math.max(0, live.breakMs))}</span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="ulc-footer">
                      <span className="ulc-foot-item"><FiUsers className="ulc-foot-icon" /> {u.assigned_clients || 0}</span>
                      <span className="ulc-foot-dot" />
                      <span className="ulc-foot-item ulc-foot-hover-wrap">
                        <FiClock className="ulc-foot-icon ulc-fi-green" /> თვე {fmt(Math.max(0, u.month_work_seconds || 0) * 1000)}
                        <FiInfo className="ulc-foot-icon ulc-fi-info" />
                        <div className="ulc-foot-tooltip">
                          <div className="ulc-ft-row"><span>დღეს <span className="ulc-ft-date">{new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span></span><span>{fmt(Math.max(0, u.today_work_seconds || 0) * 1000)}</span></div>
                          <div className="ulc-ft-row"><span>კვირა <span className="ulc-ft-date">{(() => { const d = new Date(); const day = d.getDay() || 7; const mon = new Date(d); mon.setDate(d.getDate() - day + 1); const sun = new Date(mon); sun.setDate(mon.getDate() + 6); const f = (dt) => dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); return `${f(mon)} - ${f(sun)}`; })()}</span></span><span>{fmt(Math.max(0, u.week_work_seconds || 0) * 1000)}</span></div>
                          <div className="ulc-ft-row"><span>თვე <span className="ulc-ft-date">{(() => { const d = new Date(); const start = new Date(d.getFullYear(), d.getMonth(), 1); const end = new Date(d.getFullYear(), d.getMonth() + 1, 0); const f = (dt) => dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); return `${f(start)} - ${f(end)}`; })()}</span></span><span>{fmt(Math.max(0, u.month_work_seconds || 0) * 1000)}</span></div>
                          <div className="ulc-ft-row"><span>სულ</span><span>{fmt(Math.max(0, u.total_work_seconds || 0) * 1000)}</span></div>
                        </div>
                      </span>
                    </div>
                  </div>
                );
              })}
              {filteredUsers.length === 0 && <div className="empty-state-card">თანამშრომლები არ არის</div>}
            </div>
          </>
        )}
      </div>
    );
  }

  // Detail view
  const filteredSessions = filterByPeriod(userDetail?.sessions, 'started_at')
    ?.slice()
    .sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
  const stats = computeStats(filteredSessions);

  return (
    <div className="page">
      <div className="page-header">
        <div className="detail-header">
          <button className="btn-back" onClick={goBack}><FiArrowLeft /></button>
          <div>
            <h1>{selectedUser.first_name} {selectedUser.last_name}</h1>
            <p className="page-subtitle">{selectedUser.personal_number}</p>
          </div>
        </div>
      </div>

      {detailLoading ? <div className="loading">იტვირთება...</div> : userDetail && (
        <>
          {/* Period filter */}
          <div className="period-filter-wrap" style={{ marginBottom: 16 }}>
            <div className="period-filter">
              {PERIODS.map((p) => {
                let dateHint = '';
                const now = new Date();
                const fmtD = (dt) => dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                if (p.key === 'today') dateHint = fmtD(now);
                else if (p.key === 'week') {
                  const day = now.getDay() || 7;
                  const mon = new Date(now); mon.setDate(now.getDate() - day + 1);
                  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
                  dateHint = `${fmtD(mon)} - ${fmtD(sun)}`;
                } else if (p.key === 'month') {
                  const start = new Date(now.getFullYear(), now.getMonth(), 1);
                  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                  dateHint = `${fmtD(start)} - ${fmtD(end)}`;
                }
                return (
                  <button key={p.key} className={`period-btn ${period === p.key ? 'active' : ''}`} onClick={() => setPeriod(p.key)}>
                    {p.label}
                    {dateHint && <span className="period-date-hint">{dateHint}</span>}
                  </button>
                );
              })}
            </div>
            <div className={`date-range-picker ${period === 'custom' ? 'active' : ''}`}>
              <Calendar compact value={dateFrom} onChange={(v) => { setDateFrom(v); setPeriod('custom'); }} placeholder="დან" max={dateTo || todayStr} />
              <span className="drp-sep">—</span>
              <Calendar compact value={dateTo} onChange={(v) => { setDateTo(v); setPeriod('custom'); }} placeholder="მდე" max={todayStr} min={dateFrom || undefined} />
            </div>
          </div>

          {/* Summary stats */}
          <div className="stats-grid">
            <div className="stat-card stat-blue">
              <div className="stat-icon"><FiClock /></div>
              <div className="stat-body">
                <span className="stat-number">{fmt(stats.totalWork)}</span>
                <span className="stat-label">სამუშაო დრო</span>
              </div>
            </div>
            <div className="stat-card stat-orange">
              <div className="stat-icon"><FiCoffee /></div>
              <div className="stat-body">
                <span className="stat-number">{fmt(stats.totalBreak)}</span>
                <span className="stat-label">შესვენება</span>
              </div>
            </div>
            <div className="stat-card stat-green">
              <div className="stat-icon"><FiCalendar /></div>
              <div className="stat-body">
                <span className="stat-number">{stats.sessionCount}</span>
                <span className="stat-label">სესიები</span>
              </div>
            </div>
            <div className="stat-card stat-purple">
              <div className="stat-icon"><FiUsers /></div>
              <div className="stat-body">
                <span className="stat-number">{userDetail.clients.length}</span>
                <span className="stat-label">ბენეფიციარი</span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="detail-tabs">
            <button className={`detail-tab ${activeTab === 'sessions' ? 'active' : ''}`} onClick={() => setActiveTab('sessions')}>
              <FiClock /> სესიები
            </button>
            <button className={`detail-tab ${activeTab === 'clients' ? 'active' : ''}`} onClick={() => setActiveTab('clients')}>
              <FiUsers /> ბენეფიციარი
            </button>
          </div>

          {/* Sessions tab */}
          {activeTab === 'sessions' && (
            <div className="tab-content">
              <div className="sessions-list scrollable-list">
                {filteredSessions && filteredSessions.length > 0 ? filteredSessions.map((s) => {
                  const start = new Date(s.started_at);
                  const end = s.ended_at ? new Date(s.ended_at) : null;
                  const now = new Date();
                  const effectiveEnd = end || now;
                  let totalBreak = 0;
                  let activeBreakMs = 0;
                  (s.breaks || []).forEach((b) => {
                    if (b.ended_at) totalBreak += new Date(b.ended_at) - new Date(b.started_at);
                    else activeBreakMs = now - new Date(b.started_at);
                  });
                  const workMs = (effectiveEnd - start) - totalBreak - activeBreakMs;
                  const isLive = !end;
                  const attachments = getSessionAttachments(s.id);
                  const isExpanded = expandedSession === s.id;

                  return (
                    <div key={s.id} className={`session-card ${isExpanded ? 'expanded' : ''}`}>
                      <div className="sess-header" onClick={() => setExpandedSession(isExpanded ? null : s.id)}>
                        <div className="sess-header-left">
                          <span className={`sess-status ${s.status}`}>{s.status === 'completed' ? 'დასრულებული' : s.status === 'break' ? 'შესვენებაზე' : 'აქტიური'}</span>
                          <span className="sess-date-text">{fmtDate(start)}</span>
                        </div>
                        <div className="sess-header-right">
                          <span className="sess-summary">{fmtTime(start)} - {end ? fmtTime(end) : '...'}</span>
                          <span className={`sess-total-time ${isLive ? 'sess-live' : ''}`}>{isLive ? fmtLive(Math.max(0, workMs)) : fmt(Math.max(0, workMs))}</span>
                          {isLive && activeBreakMs > 0 && <span className="sess-break-time"><FiCoffee /> {fmtLive(activeBreakMs)}</span>}
                          <button className="btn-icon btn-danger btn-sm" onClick={(e) => { e.stopPropagation(); deleteSession(s); }} title="სესიის წაშლა"><FiTrash2 /></button>
                          {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="sess-detail">
                          {/* Work/Break summary */}
                          <div className="sess-detail-row">
                            <div className="sdr-item"><span className="sdr-label">დაწყება</span><span className="sdr-value">{fmtTime(start)}</span></div>
                            {end && <div className="sdr-item"><span className="sdr-label">დასრულება</span><span className="sdr-value">{fmtTime(end)}</span></div>}
                            <div className="sdr-item sdr-work"><span className="sdr-label">სამუშაო</span><span className="sdr-value">{isLive ? fmtLive(Math.max(0, workMs)) : fmt(Math.max(0, workMs))}</span></div>
                            {(totalBreak > 0 || activeBreakMs > 0) && <div className="sdr-item sdr-break"><span className="sdr-label">შესვენება</span><span className="sdr-value">{isLive ? fmtLive(totalBreak + activeBreakMs) : fmt(totalBreak + activeBreakMs)}</span></div>}
                          </div>

                          {/* Breaks */}
                          {s.breaks && s.breaks.length > 0 && (
                            <div className="sess-breaks-section">
                              <div className="sbs-title"><FiCoffee /> შესვენებები</div>
                              {s.breaks.map((b, bi) => (
                                <div key={bi} className="break-row">
                                  <span>{fmtTime(new Date(b.started_at))}</span>
                                  <span className="break-arrow">→</span>
                                  <span>{b.ended_at ? fmtTime(new Date(b.ended_at)) : 'მიმდინარე'}</span>
                                  <span className="break-dur">{b.ended_at ? fmt(new Date(b.ended_at) - new Date(b.started_at)) : fmtLive(Date.now() - new Date(b.started_at).getTime())}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Attachments filled in this session */}
                          {attachments.length > 0 && (
                            <div className="sess-attachments-section">
                              <div className="sbs-title"><FiFileText /> შევსებული დანართები</div>
                              {attachments.map((fa) => (
                                <div key={fa.id} className="sess-att-row" onClick={() => openView(fa)} style={{ cursor: 'pointer' }}>
                                  <FiEye style={{ color: '#14b8a6', flexShrink: 0 }} />
                                  <span className="sa-title">{fa.template_title || 'დანართი'}</span>
                                  <span className="sa-client">{fa.client_first_name} {fa.client_last_name}</span>
                                  {fa.duration_seconds && <span className="sa-dur"><FiClock /> {fmt(fa.duration_seconds * 1000)}</span>}
                                  <button className="btn-icon btn-danger" onClick={(e) => { e.stopPropagation(); deleteAttachment(fa); }} title="სამუდამო წაშლა"><FiTrash2 /></button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }) : <div className="empty-state">სესიები არ არის ამ პერიოდში</div>}
              </div>
            </div>
          )}

          {/* Clients tab */}
          {activeTab === 'clients' && (
            <div className="tab-content">
              <div className="assigned-chips scrollable-list">
                {userDetail.clients.length > 0 ? userDetail.clients.map((c) => (
                  <div key={c.id} className="client-chip">
                    <span className="chip-avatar">{c.first_name[0]}{c.last_name[0]}</span>
                    {c.first_name} {c.last_name} <span style={{color:'#94a3b8', fontSize: 12}}>({c.personal_number})</span>
                  </div>
                )) : <div className="empty-state">ბენეფიციარები არ არის</div>}
              </div>
            </div>
          )}
        </>
      )}

      {/* View filled attachment modal */}
      {showView && viewData && (
        <div className="modal-overlay" onClick={() => setShowView(false)}>
          <div className="modal attachment-modal print-area" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header no-print">
              <h3>{viewData.template_title}</h3>
              <span className="fill-date-badge">
                <FiCalendar />
                {(() => { const d = new Date(viewData.completed_at); const days = ['კვირა','ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი']; return `${days[d.getDay()]}, ${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-icon" onClick={printAttachment} title="ბეჭდვა"><FiPrinter /></button>
                <button className="btn-icon" onClick={() => setShowView(false)}><FiX /></button>
              </div>
            </div>
            <div className="paper">
              <div className="paper-title-right">{viewData.template_title}</div>
              {viewData.template_main_title && <div className="paper-center"><div className="paper-main-title">{viewData.template_main_title}</div></div>}
              {viewData.template_subtitle && <div className="paper-center"><div className="paper-subtitle">{viewData.template_subtitle}</div></div>}
              <div className="paper-fields">
                {(viewData.template_header_fields || []).map((f, i) => (
                  <div key={i} className="paper-field">
                    <span className="paper-field-label">{f.label}</span>
                    <span className="paper-field-value">
                      {f.type === 'client_dropdown' ? `${viewData.client_first_name || ''} ${viewData.client_last_name || ''} - ${viewData.client_pn || ''}` :
                       f.type === 'auto_user' ? `${viewData.user_first_name || ''} ${viewData.user_last_name || ''} - ${viewData.user_pn || ''}` :
                       (viewData.header_values || {})[i] || ''}
                    </span>
                  </div>
                ))}
              </div>
              {(() => {
                const cols = (viewData.template_columns || []).map((c, i) => ({ ...c, idx: i }));
                const rd = viewData.row_data || {};

                // Weekly plan view
                if (rd.type === 'weekly') {
                  const days = rd.days || {};
                  const weekStart = rd.weekStart;
                  const visibleCols = cols.filter(c => c.type !== 'datetime' && c.type !== 'duration');
                  const dayKeys = Object.keys(days).filter(k => k !== '_fullRow' && k !== '_skipDays' && !isNaN(parseInt(k))).sort((a, b) => a - b);
                  const dayNames = ['ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი','კვირა'];
                  let weekLabel = weekStart || '';
                  if (weekStart) {
                    const ws = new Date(weekStart);
                    const we = new Date(ws);
                    we.setDate(we.getDate() + 6);
                    weekLabel = `${ws.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} — ${we.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
                  }

                  return (
                    <div className="paper-table-wrap">
                      {weekStart && <div className="paper-week-label" style={{marginBottom: 12}}>კვირა: {weekLabel}</div>}
                      <table className="paper-table">
                        <thead><tr>
                          <th>დღე</th>
                          <th>თარიღი</th>
                          {visibleCols.map((c) => <th key={c.idx} className={c.fullRow ? 'paper-col-fullrow' : ''} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}</th>)}
                        </tr></thead>
                        <tbody>
                          {dayKeys.map((dayIdx, di) => {
                            const raw = days[dayIdx] || {};
                            const dayData = Array.isArray(raw) ? raw[0] || {} : raw;
                            const isSkipped = dayData._skipped;
                            const monday = weekStart ? new Date(weekStart) : null;
                            let dateStr = '';
                            if (monday) { const d = new Date(monday); d.setDate(d.getDate() + parseInt(dayIdx)); dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
                            return (
                              <tr key={dayIdx} style={isSkipped ? {opacity: 0.4} : {}}>
                                <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{dayNames[dayIdx] || `დღე ${parseInt(dayIdx) + 1}`}</td>
                                <td style={{ whiteSpace: 'nowrap' }}>{dateStr}</td>
                                {visibleCols.map((c) => {
                                  if (c.fullRow && di === 0) {
                                    const fullRowObj = days._fullRow || {};
                                    const val = fullRowObj[c.idx] || fullRowObj[String(c.idx)] || (dayKeys.length > 0 ? (days[dayKeys[0]]?.[c.idx] || '') : '');
                                    return (
                                      <td key={c.idx} rowSpan={dayKeys.length} className="paper-cell-fullrow" style={{ verticalAlign: 'top', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                                        {c.type === 'signature' && val ? <img src={val} alt="" style={{ height: 25, objectFit: 'contain' }} /> : val}
                                      </td>
                                    );
                                  }
                                  if (c.fullRow) return null;
                                  const cellVal = dayData[c.idx] || '';
                                  const strVal = typeof cellVal === 'string' ? cellVal : String(cellVal || '');
                                  return (
                                    <td key={c.idx} style={{ whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                                      {isSkipped ? '—' : c.type === 'signature' && cellVal ? <img src={cellVal} alt="" style={{ height: 25, objectFit: 'contain' }} /> : c.type === 'worktime' && strVal ? (
                                        <span className="tp-view">{strVal.split(' - ').map((t, ti) => <span key={ti} className="tp-view-time">{t}</span>).reduce((a, b) => [a, <span key="arrow" className="tp-view-arrow">→</span>, b])}</span>
                                      ) : c.type === 'activity' && strVal && strVal.includes(' || ') ? (
                                        strVal.split(' || ').filter(Boolean).map((act, ai) => <div key={ai} style={{ padding: '1px 0' }}>• {act}</div>)
                                      ) : strVal}
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                }

                // Calendar view
                if (rd.type === 'calendar') {
                  const days = rd.days || {};
                  const weekStart = rd.weekStart;
                  const dayNames = ['ორშაბათი','სამშაბათი','ოთხშაბათი','ხუთშაბათი','პარასკევი','შაბათი','კვირა'];
                  let weekLabel = weekStart || '';
                  if (weekStart) {
                    const ws = new Date(weekStart);
                    const we = new Date(ws);
                    we.setDate(we.getDate() + 6);
                    weekLabel = `${ws.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} — ${we.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
                  }
                  return (
                    <div className="paper-calendar-view">
                      {weekStart && <div className="paper-week-label">კვირა: {weekLabel}</div>}
                      {Object.keys(days).sort((a, b) => a - b).map((dayIdx) => {
                        const entries = days[dayIdx] || [];
                        return (
                          <div key={dayIdx} className="paper-cal-day">
                            <div className="paper-cal-day-header"><strong>{dayNames[dayIdx]}</strong></div>
                            {entries.length > 0 && entries.some(e => Object.values(e).some(v => v)) ? (
                              <table className="paper-table" style={{marginBottom: 0}}>
                                <thead><tr><th className="paper-col-num">№</th>
                                  {cols.map((c) => <th key={c.idx}>{c.name}</th>)}
                                </tr></thead>
                                <tbody>
                                  {entries.filter(e => Object.values(e).some(v => v)).map((entry, ei) => (
                                    <tr key={ei}>
                                      <td className="paper-col-num">{ei + 1}</td>
                                      {cols.map((c) => <td key={c.idx}>{entry[c.idx] || ''}</td>)}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div style={{padding: '8px 12px', color: '#94a3b8', fontSize: 13}}>ჩანაწერები არ არის</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // Standard view
                const rows = rd.rows || (Array.isArray(rd) ? rd : []);
                const rawFullRows = rd.fullRows || {};
                const fullRows = {};
                Object.keys(rawFullRows).forEach(k => { fullRows[Number(k)] = rawFullRows[k]; });
                const rowCount = Math.max(rows.length, 1);

                return (
                  <div className="paper-table-wrap">
                    <table className="paper-table">
                      <thead><tr><th className="paper-col-num">№</th>
                        {cols.map((c) => (<th key={c.idx} className={c.fullRow ? 'paper-col-fullrow' : ''} style={c.width ? {width: `${c.width}%`} : undefined}>{c.name}</th>))}
                      </tr></thead>
                      <tbody>
                        {rows.map((row, ri) => (
                          <tr key={ri}>
                            <td className="paper-col-num">{ri + 1}</td>
                            {cols.map((c) => {
                              if (c.fullRow && ri === 0) {
                                const items = fullRows[c.idx] || [];
                                return (
                                  <td key={c.idx} rowSpan={rowCount} className="paper-cell-fullrow" style={{ verticalAlign: 'middle', fontSize: 13 }}>
                                    {items.map((val, vi) => (
                                      <div key={vi} style={{ padding: '2px 0' }}>
                                        {c.type === 'signature' && val ? <img src={val} alt="" style={{ height: 25, objectFit: 'contain' }} /> : c.type === 'activity' && val && val.includes(' || ') ? (
                                          val.split(' || ').filter(Boolean).map((act, ai) => <div key={ai} style={{ padding: '1px 0' }}>• {act}</div>)
                                        ) : (val || '')}
                                      </div>
                                    ))}
                                  </td>
                                );
                              }
                              if (c.fullRow) return null;
                              return (
                                <td key={c.idx}>
                                  {c.type === 'signature' && row[c.idx] ? <img src={row[c.idx]} alt="" style={{ height: 25, objectFit: 'contain' }} /> : (row[c.idx] || '')}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              {viewData.note && <div className="paper-note"><strong>შენიშვნა:</strong> {viewData.note}</div>}
              {false && viewData.duration_seconds && (
                <div className="paper-duration">ხანგრძლივობა: {formatTimeSec(viewData.duration_seconds)}</div>
              )}
            </div>
          </div>
        </div>
      )}
      {confirmAction && <ConfirmModal {...confirmAction} onCancel={() => setConfirmAction(null)} />}
    </div>
  );
};

export default Logs;
